import { AlertTriangle, CheckCircle2, CircleX } from 'lucide-react';
import type { ValidationIssue } from '../../domain/validation';
import type { Language, TranslationKey } from '../../i18n';
import styles from './ValidationPanel.module.css';

interface ValidationPanelProps {
  issues: ValidationIssue[];
  language: Language;
  compact?: boolean;
  t: (key: TranslationKey, values?: Record<string, string | number>) => string;
  onIssueClick: (issue: ValidationIssue) => void;
}

const zhMessages: Record<string, string> = {
  'filename.invalid': '文件名只能使用安全字符，并且必须以 .yml 或 .yaml 结尾。',
  'name.required': '名称不能为空。',
  'description.required': '说明不能为空。',
  'title.invalid': '默认标题必须是字符串。',
  'body.required': '表单至少需要一个字段。',
  'field.invalid': '字段结构无效。',
  'field.type.unknown': '字段类型不受支持。',
  'field.attributes.invalid': '字段属性结构无效。',
  'markdown.value.required': 'Markdown 内容不能为空。',
  'field.id.required': '非 Markdown 字段必须有 ID。',
  'field.id.invalid': 'ID 只能包含字母、数字、短横线和下划线。',
  'field.id.duplicate': 'ID 不能重复。',
  'field.label.required': '字段标签不能为空。',
  'field.required.invalid': 'required 必须是布尔值。',
  'dropdown.options.required': '下拉框至少需要一个选项。',
  'dropdown.option.empty': '下拉框选项不能为空。',
  'dropdown.option.duplicate': '下拉框选项不能重复。',
  'checkboxes.options.required': '复选框至少需要一个选项。',
  'checkboxes.option.label.required': '每个复选框选项都需要标签。',
  'checkboxes.option.required.invalid': '选项的 required 必须是布尔值。',
  'labels.invalid': '标签必须是字符串列表。',
  'labels.normalized': '空标签和重复标签会被移除。',
  'assignees.invalid': '负责人必须是字符串列表。',
  'assignees.normalized': '空负责人和重复负责人会被移除。',
};

function issueMessage(issue: ValidationIssue, language: Language): string {
  return language === 'zh' ? (zhMessages[issue.code] ?? issue.message) : issue.message;
}

export function ValidationPanel({
  issues,
  language,
  compact = false,
  t,
  onIssueClick,
}: ValidationPanelProps) {
  if (issues.length === 0) {
    return compact ? null : (
      <section className={styles.empty}>
        <CheckCircle2 size={18} strokeWidth={1.8} aria-hidden="true" />
        <span>{t('validationEmpty')}</span>
      </section>
    );
  }

  return (
    <section className={compact ? styles.compactPanel : styles.panel} aria-label={t('validation')}>
      {!compact ? <h2>{t('validation')}</h2> : null}
      <div className={styles.list}>
        {issues.map((issue, index) => {
          const Icon = issue.severity === 'error' ? CircleX : AlertTriangle;
          return (
            <button
              type="button"
              className={styles.issue}
              data-severity={issue.severity}
              data-testid="validation-item"
              key={`${issue.code}-${issue.path}-${index}`}
              onClick={() => onIssueClick(issue)}
            >
              <Icon size={15} strokeWidth={1.8} aria-hidden="true" />
              <span>
                <strong>{issue.path}</strong>
                {issueMessage(issue, language)}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
